/**
 * Where the ship is, against where a station is.
 *
 * The geometry of this — polygons, slabs, boxes on top of them — lives in
 * `stationHit` and is unit-tested there. What lives here is the part that kept
 * going wrong, which is not geometry at all: three coordinate spaces meet at
 * this call, and every one of them looks like a `Vector3`.
 *
 *   - `shipAt` is absolute, in-system, and can be tens of thousands of units out;
 *   - the scene is shifted every frame to put the ship at the origin, so a
 *     station's world matrix reads as a few hundred units, not a few thousand;
 *   - the deck's own frame is in generator units, and carries the station's
 *     orbit, its spin and the scene's `GAME_SCALE`.
 *
 * Mixing the first two silently put every station its full orbital radius away,
 * so the broad phase rejected all of them and nothing collided anywhere, ever.
 * Nothing in a type says which space a vector is in, and nothing in
 * `stationHit`'s own tests could see it.
 *
 * So the reconciliation is here, in one function, called by the flight loop and
 * by the test — not written out twice. A test that reimplements the frame is
 * testing its own copy of the frame, which is how the bug survived being tested
 * the first time.
 */

import * as THREE from 'three';

import {
  hitsVolume,
  pushOutOf,
  type HullShadow,
  type ShipFootprint,
  type StationVolume,
} from '@interspacial/shared';

import { GAME_SCALE } from './systemScene';

/** A station as the scene builder hands it over. */
export interface StationHull {
  deck: THREE.Object3D;
  /** In generator units — `GAME_SCALE` is carried by the scene group. */
  radius: number;
  volumes: readonly StationVolume[];
}

export interface StationImpact {
  /** Which part was hit, for the damage report. */
  struck: StationVolume;
  /** Which way out, as a unit vector in world space. */
  normal: THREE.Vector3;
}

/**
 * How far outside a station's own radius the narrow phase is worth running.
 *
 * Stations are small — tens of world units across — so this is deliberately
 * many times a station's size rather than a tight fit. The narrow phase is a
 * point test against a few dozen boxes; the broad phase exists to skip the
 * stations that are half a system away, not to shave a polygon test.
 */
export const STATION_HIT_MARGIN = 400;

const local = new THREE.Vector3();
const deckAt = new THREE.Vector3();
const shipToDeck = new THREE.Matrix4();
const shipWorld = new THREE.Matrix4();
const corner = new THREE.Vector3();
const heading = new THREE.Vector3();
const deckInverse = new THREE.Matrix4();

/**
 * The ship's real outline, carried into a deck's own frame.
 *
 * This is what makes the hull a shape rather than a radius. Every vertex goes
 * through the ship's orientation and the deck's, so a hull turned side-on to an
 * edge presents its width — which is the manoeuvre a pilot makes to slip past
 * something, and the one a circle could not represent.
 *
 * The height range is taken from the transformed vertices and then opened by the
 * hull's own thickness, so a tilted ship reports the range it really occupies.
 */
function shadowOf(
  footprint: ShipFootprint,
  deck: THREE.Object3D,
  shipAt: THREE.Vector3,
  origin: THREE.Vector3,
  facing: THREE.Quaternion,
): HullShadow {
  shipWorld.compose(local.copy(shipAt).sub(origin), facing, ONE);
  shipToDeck.copy(deck.matrixWorld).invert().multiply(shipWorld);

  const outline: { x: number; y: number }[] = [];
  let low = Infinity;
  let high = -Infinity;

  for (const point of footprint.outline) {
    corner.set(point.x, point.y, 0).applyMatrix4(shipToDeck);
    outline.push({ x: corner.x, y: corner.y });
    low = Math.min(low, corner.z);
    high = Math.max(high, corner.z);
  }

  // The hull's own frame is scaled by `GAME_SCALE` relative to the deck's, like
  // everything else that crosses this boundary.
  const half = footprint.depth / GAME_SCALE;

  local.copy(shipAt).sub(origin);
  deck.worldToLocal(local);

  return { outline, centre: { x: local.x, y: local.y, z: local.z }, low: low - half, high: high + half };
}

const ONE = new THREE.Vector3(1, 1, 1);

/**
 * The first station part this hull is inside, or null.
 *
 * `shipAt` and `origin` are both absolute; the difference between them is the
 * ship's place in the scene, which is what the deck's matrix can be compared
 * against. Taken as two vectors rather than one pre-rebased one because the
 * flight loop does not have the rebased position to hand at this point in the
 * frame — `hull.position` is not written until later — and passing the wrong
 * one of the two is the mistake this whole module exists to prevent.
 */
export function stationImpact(
  hulls: readonly StationHull[],
  shipAt: THREE.Vector3,
  origin: THREE.Vector3,
  facing: THREE.Quaternion,
  footprint: ShipFootprint,
  travel?: THREE.Vector3,
): StationImpact | null {
  for (const station of hulls) {
    local.copy(shipAt).sub(origin);
    station.deck.getWorldPosition(deckAt);

    if (deckAt.distanceTo(local) > station.radius * GAME_SCALE + STATION_HIT_MARGIN) continue;

    const shadow = shadowOf(footprint, station.deck, shipAt, origin, facing);

    const struck = station.volumes.find((volume) => hitsVolume(volume, shadow));
    if (!struck) continue;

    // Out in the deck's frame, then rotated into the world so the ship is
    // pushed off the face it actually hit rather than away from the middle.
    // The hull's motion in the deck's own frame, so the way out can be chosen
    // to actually resist it — see `pushOutOf`.
    let approach: { x: number; y: number; z: number } | undefined;
    if (travel) {
      heading.copy(travel).transformDirection(deckInverse.copy(station.deck.matrixWorld).invert());
      approach = { x: heading.x, y: heading.y, z: heading.z };
    }

    const out = pushOutOf(struck, shadow, approach);
    const normal = new THREE.Vector3(out.x, out.y, out.z)
      .transformDirection(station.deck.matrixWorld)
      .normalize();

    return { struck, normal };
  }

  return null;
}
